import React, { useEffect } from 'react';
import { Table, Row, Col, FormGroup, Label } from 'reactstrap';

import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
  useAsyncDebounce,
  useRowSelect,
} from 'react-table';

import Select from 'react-select';
import { Icon } from '@assets';
import { useHistory } from 'react-router-dom';

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <>
        <input
          type='checkbox'
          className='custom-checkbox'
          ref={resolvedRef}
          {...rest}
        />
      </>
    );
  }
);

function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);

  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <div className='search-bar d-inline-block inline-block mr-2 mt-2 mt-md-0'>
      <FormGroup className='mb-0 w-auto d-inline-block'>
        <Label for='appartment' className='mb-0 w-auto d-inline-block mr-2'>
          <Icon icon='search' />
        </Label>
        <input
          value={value || ''}
          onChange={(e) => {
            setValue(e.target.value);
            onChange(e.target.value);
          }}
          placeholder='Search by keywords'
        />
      </FormGroup>
    </div>
  );
}

const statusColorForMobile = (status) => {
  if (!status) return '#6b7280';
  const normalized = status.toLowerCase();

  if (normalized.includes('active')) return '#2cba7c';
  if (normalized.includes('pending') || normalized.includes('follow')) return '#f2994a';
  if (normalized.includes('inactive') || normalized.includes('archive')) return '#9ca3af';
  if (normalized.includes('hot')) return '#f25f5c';
  return '#2563eb';
};

const mobileStyles = {
  actionsWrapper: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  actionButton: {
    flex: '1 1 140px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 14px',
    borderRadius: '12px',
    boxShadow: '0 6px 16px rgba(15, 34, 58, 0.12)',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  listWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    paddingBottom: '72px',
  },
  card: {
    background: '#ffffff',
    borderRadius: '16px',
    padding: '16px',
    boxShadow: '0 10px 30px rgba(15, 34, 58, 0.08)',
    border: '1px solid #eef2f7',
    cursor: 'pointer',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '4px',
    color: '#1f2933',
  },
  subtitle: {
    fontSize: '13px',
    color: '#6b7280',
    lineHeight: 1.35,
    marginBottom: '6px',
  },
  statusBadge: (status) => ({
    fontSize: '13px',
    fontWeight: 600,
    color: statusColorForMobile(status),
    background: `${statusColorForMobile(status)}12`,
    borderRadius: '999px',
    padding: '6px 12px',
    minWidth: '80px',
    textAlign: 'center',
    textTransform: 'capitalize',
  }),
  detailSection: {
    marginTop: '10px',
    borderTop: '1px solid #f1f5f9',
    paddingTop: '10px',
    display: 'grid',
    rowGap: '10px',
  },
  detailRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  detailLabel: {
    fontSize: '12px',
    color: '#94a3b8',
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
  },
  detailValue: {
    fontSize: '13px',
    color: '#334155',
    fontWeight: 500,
  },
};

export default function ClientListTable({ columns, data, addons, changeIsChecked, setSidebarState, leftAddons=null }) {
  var totalRecord = data.length;
  const pageSizeOptions = [
    { value: '10', label: '10' },
    { value: '15', label: '15' },
    { value: '25', label: '25' },
    { value: '50', label: '50' },
    { value: '75', label: '75' },
    { value: '100', label: '100' },
    { value: totalRecord.toString(), label: 'All' },
  ];
  const defaultColumn = React.useMemo(
    () => ({
      width: 'auto',
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    preGlobalFilteredRows,
    setGlobalFilter,
    selectedFlatRows,
    rows,
    state: { pageIndex, pageSize, globalFilter, selectedRowIds },
    prepareRow,
  } = useTable(
    { columns, data, defaultColumn, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) =>
        changeIsChecked
          ? [
              // Let's make a column for selection
              {
                id: 'selection',
                Header: ({ getToggleAllPageRowsSelectedProps }) => (
                  <IndeterminateCheckbox
                    {...getToggleAllPageRowsSelectedProps()}
                  />
                ),
                Cell: ({ row }) => (
                  <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} onClick={(e) => e.stopPropagation()}/>
                ),
                width: 50,
                maxWidth: 50,
              },
              ...columns,
            ]
          : columns
      );
    }
  );

  useEffect(() => {
    if (changeIsChecked) {
      changeIsChecked(selectedFlatRows);
    }
  }, [selectedFlatRows]);

	const history = useHistory();

  return (
    <>
      <Row className='mb-2 d-flex align-items-start'>
        <Col sm='6'>
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
					{leftAddons}
        </Col>

        <Col sm='6' className='text-lg-right mt-2 mt-md-0 d-none d-sm-block'>
          {addons}
        </Col>
      </Row>

      {/* Desktop View - Table Design */}
      <div className='d-none d-sm-block'>
        <Table
          responsive
          className='client-list text-left min-w-900'
          {...getTableProps()}
        >
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps([
                      {
                        style: {
                          minWidth: column.minWidth,
                          width: column.width,
                          maxWidth: column.maxWidth,
                        },
                      },
                      column.getSortByToggleProps(),
                    ])}
                  >
                    {column.render('Header')}
                    <span>
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <>
                            &nbsp; <Icon icon='sort-down' size='lg' />
                          </>
                        ) : (
                          <>
                            &nbsp; <Icon icon='sort-up' size='lg' />
                          </>
                        )
                      ) : column.canSort ? (
                        <>
                          &nbsp; <Icon icon='sort' size='lg' />
                        </>
                      ) : (
                        ''
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}
                onClick={() => {
                  setSidebarState(true);
                  history.push(`/clients/show/${row.original.node.id}`)
                }}
                >
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                    );
                  })}
                </tr>
              );
            })}
            {page.length <= 2 ? <tr><div className='spacer-div'></div></tr> : null}
          </tbody>
        </Table>
      </div>

      {/* Mobile View - Enhanced List Design */}
      <div className='d-block d-sm-none'>
        {addons ? (
          <div style={mobileStyles.actionsWrapper}>
            {React.Children.map(addons, (child, index) => {
              if (!React.isValidElement(child)) return child;
              const existingClasses = child.props.className || '';
              const cleanedClasses = existingClasses
                .split(' ')
                .filter((cls) => cls !== 'float-right' && cls !== 'mt-sm-0' && cls !== 'mr-2')
                .join(' ')
                .trim();

              return React.cloneElement(child, {
                key: index,
                className: `${cleanedClasses} mobile-action-btn`.trim(),
                style: { ...mobileStyles.actionButton, ...(child.props.style || {}) },
              });
            })}
          </div>
        ) : null}

        <div style={mobileStyles.listWrapper}>
          {page.map((row) => {
            prepareRow(row);
            const availableCells = row.cells.filter((cell) => cell.column.id !== 'selection');
            const selectionCell = row.cells.find((cell) => cell.column.id === 'selection');
            const primaryCell =
              availableCells.find((cell) => cell.column.Header === 'Client') || availableCells[0];
            const statusCell = availableCells.find((cell) => cell.column.Header === 'Status');
            const secondaryCells = availableCells.filter(
              (cell) => cell !== primaryCell && cell !== statusCell
            );
            const summaryPreference = ['Agent', 'Market', 'M/I Date', 'Next Follow Up'];
            const topMeta = [];
            summaryPreference.forEach((label) => {
              if (topMeta.length >= 2) return;
              const found = secondaryCells.find((cell) => cell.column.Header === label);
              if (found && !topMeta.includes(found)) {
                topMeta.push(found);
              }
            });
            if (topMeta.length < 2) {
              secondaryCells.forEach((cell) => {
                if (topMeta.length >= 2) return;
                if (!topMeta.includes(cell)) {
                  topMeta.push(cell);
                }
              });
            }
            const detailCells = secondaryCells.filter((cell) => !topMeta.includes(cell));
            const primaryContent = primaryCell ? primaryCell.render('Cell') : 'Client Details';
            const statusValue =
              row.original?.node?.currentStatus ??
              (statusCell ? row.values?.[statusCell.column.id] : '');

            // Determine which column ID to skip in the body (if a specific primary ID column is used)
            const primaryColumnId = primaryCell ? primaryCell.column.id : null;

            return (
              <div
                key={row.id}
                style={mobileStyles.card}
                onClick={() => {
                  setSidebarState(true);
                  history.push(`/clients/show/${row.original.node.id}`);
                }}
              >
                <div style={mobileStyles.headerRow}>
                  <div style={{ flex: '1 1 auto' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {selectionCell ? (
                        <span onClick={(e) => e.stopPropagation()}>
                          {selectionCell.render('Cell')}
                        </span>
                      ) : null}
                      <div style={mobileStyles.title}>{primaryContent}</div>
                    </div>
                    {topMeta.map((cell) => (
                      <div key={cell.column.id} style={mobileStyles.subtitle}>
                        {cell.render('Cell')}
                      </div>
                    ))}
                  </div>
                  {statusCell ? (
                    <div
                      style={mobileStyles.statusBadge(statusValue)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {statusValue || 'Status'}
                    </div>
                  ) : null}
                </div>

                {detailCells.length > 0 || statusCell ? (
                  <div style={mobileStyles.detailSection}>
                    {statusCell ? (
                      <div style={mobileStyles.detailRow} onClick={(e) => e.stopPropagation()}>
                        <span style={mobileStyles.detailLabel}>Update Status</span>
                        <div style={mobileStyles.detailValue}>{statusCell.render('Cell')}</div>
                      </div>
                    ) : null}
                    {detailCells.map((cell) => (
                      <div key={cell.column.id} style={mobileStyles.detailRow}>
                        <span style={mobileStyles.detailLabel}>{cell.column.Header}</span>
                        <div style={mobileStyles.detailValue}>{cell.render('Cell')}</div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <Row className='d-flex align-items-center mt-4'>
        <Col md='6'>
          {/*  Showing first {pageSize} results of {rows.length} rows */}
          <FormGroup className='mb-3 mb-md-0'>
            <Label
              for='appartment'
              className='mb-0 w-auto d-inline-block mr-2 mr-md-3'
            >
              Showing Entries
            </Label>
            <Select
              name='PageSize'
              defaultValue={pageSizeOptions[0]}
              options={pageSizeOptions}
              onChange={(e) => {
                setPageSize(Number(e.value));
              }}
              className='basic-multi-select text-left'
              classNamePrefix='default-select'
              menuPlacement={'auto'}
            />
          </FormGroup>
        </Col>

        <Col
                 md='6'
                 className='d-flex justify-content-center mt-2 mt-md-0'
               >
          <ul className='pagination'>
            <li onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
              <a>
                {/* <Icon icon="angle-double-left" size="lg" /> */}
                <Icon icon='chevron-left' />
                <Icon icon='chevron-left' />
              </a>
            </li>
            <li onClick={() => previousPage()} disabled={!canPreviousPage}>
              <a>
                {/* <Icon icon="angle-left" size="lg" /> */}
                <Icon icon='chevron-left' />
              </a>
            </li>
            <span className='d-flex align-items-center px-3'>
              Page &nbsp; <strong>{pageIndex + 1}</strong> &nbsp; of &nbsp;
              <strong>{pageOptions.length}</strong>
            </span>
            <li onClick={() => nextPage()} disabled={!canNextPage}>
              <a>
                {/* <Icon icon="angle-right" size="lg" /> */}
                <Icon icon='chevron-right' />
              </a>
            </li>
            <li onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
              <a>
                {/* <Icon icon="angle-double-right" size="lg" /> */}
                <Icon icon='chevron-right' />
                <Icon icon='chevron-right' />
              </a>
            </li>
          </ul>
         </Col>
      </Row>
    </>
  );
}
